/**
 * Tax codes — `/finance/tax-codes`.
 *
 * Two contract facts drive this screen:
 *
 * - **Rates are effective-dated and never edited in place.** A rate change is a
 *   new row whose validity begins where the previous one ends, and overlapping
 *   validity for one code is refused (`finance.tax_rate_overlap`). So the code
 *   editor cannot touch a percentage at all: it shows the full history, marks
 *   which row is in force today, and offers "add a rate" as its own action.
 * - **`code` and `kind` freeze at creation**, because reinterpreting a code
 *   documents were already filed under would rewrite reported history. They are
 *   inputs on the create form and read-only facts on the edit form.
 *
 * Percentages are decimal strings: "15" means 15%, never 0.15, and never a JS
 * number.
 */
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import { Button, Card, CardContent, CardHeader, CardTitle, Checkbox } from "@erp/ui";
import { Plus } from "lucide-react";

import { DataTable, badgeCell, textCell } from "@/components/data-table";
import { surfaceProblem } from "@/components/form";
import { useIdempotentMutation } from "@/features/shared/ops-api";
import { formatQuantity } from "@/lib/format";

import {
  TAX_CODE_KINDS,
  ZERO_RATE_KINDS,
  addTaxRate,
  createTaxCode,
  updateTaxCode,
  type Account,
  type TaxCode,
  type TaxCodeCreate,
  type TaxCodeKind,
  type TaxCodePatch,
  type TaxRate,
  type TaxRateCreate,
} from "../api";
import { PERCENT_SCALE, normalizeDecimal, isZeroDecimal, todayIsoDate } from "../decimal";
import {
  DateField,
  DecimalField,
  Field,
  PageHeader,
  ProblemBanner,
  SelectField,
  TextField,
} from "../components/fields";
import {
  financeKeys,
  usePostableAccounts,
  useTaxCode,
  useTaxCodeList,
  type TaxCodeListQueryFilters,
} from "../queries";

const ALL = "__all__";
const NONE = "__none__";

const KIND_VARIANTS = {
  standard: "default",
  zero_rated: "secondary",
  exempt: "outline",
  reverse_charge: "warning",
} as const;

interface CodeDraft {
  code: string;
  name: string;
  nameAr: string;
  kind: TaxCodeKind;
  jurisdiction: string;
  outputAccountId: string;
  inputAccountId: string;
  active: boolean;
}

const EMPTY_DRAFT: CodeDraft = {
  code: "",
  name: "",
  nameAr: "",
  kind: "standard",
  jurisdiction: "SA",
  outputAccountId: NONE,
  inputAccountId: NONE,
  active: true,
};

interface RateDraft {
  percent: string;
  validFrom: string;
  validTo: string;
}

function emptyRateDraft(): RateDraft {
  return { percent: "", validFrom: todayIsoDate(), validTo: "" };
}

/** The rate in force on `onDate`; validity is inclusive at both ends. */
export function rateInForce(rates: readonly TaxRate[], onDate: string): TaxRate | undefined {
  return rates.find(
    (rate) => rate.valid_from <= onDate && (rate.valid_to === null || rate.valid_to >= onDate),
  );
}

export interface TaxCodesPageProps {
  canCreate?: boolean;
  canUpdate?: boolean;
  canReadAccounts?: boolean;
  /** Page size override; tests exercise cursor paging with a small limit. */
  pageLimit?: number;
}

export function TaxCodesPage({
  canCreate = true,
  canUpdate = true,
  canReadAccounts = true,
  pageLimit,
}: TaxCodesPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const [activeFilter, setActiveFilter] = useState<string>(ALL);
  const [jurisdictionFilter, setJurisdictionFilter] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<CodeDraft>(EMPTY_DRAFT);
  const [openingRate, setOpeningRate] = useState<RateDraft>(emptyRateDraft);
  const [newRate, setNewRate] = useState<RateDraft>(emptyRateDraft);
  const [formError, setFormError] = useState<string | null>(null);
  const [rateError, setRateError] = useState<string | null>(null);

  const filters = useMemo<TaxCodeListQueryFilters>(() => {
    const built: TaxCodeListQueryFilters = {};
    if (activeFilter !== ALL) built.active = activeFilter === "true";
    if (jurisdictionFilter.trim() !== "") built.jurisdiction = jurisdictionFilter.trim();
    return built;
  }, [activeFilter, jurisdictionFilter]);

  const listQuery = useTaxCodeList(filters, pageLimit);
  const detailQuery = useTaxCode(selectedId ?? undefined);
  const accountsQuery = usePostableAccounts(canReadAccounts);

  const accountOptions = useMemo(() => {
    const accounts: Account[] = accountsQuery.data ?? [];
    return [
      { value: NONE, label: t("finance.taxCodes.noAccount", "Not mapped") },
      ...accounts
        .filter((account) => account.is_postable)
        .map((account) => ({ value: account.id, label: `${account.code} · ${account.name}` })),
    ];
  }, [accountsQuery.data, t]);

  const today = todayIsoDate();
  const kindLabel = (kind: string): string => t(`finance.taxCodes.kind.${kind}`, kind);

  const resetForms = () => {
    setCreating(false);
    setDraft(EMPTY_DRAFT);
    setOpeningRate(emptyRateDraft());
    setNewRate(emptyRateDraft());
    setFormError(null);
    setRateError(null);
  };

  const selectCode = (taxCode: TaxCode) => {
    setCreating(false);
    setSelectedId(taxCode.id);
    setFormError(null);
    setRateError(null);
    setNewRate(emptyRateDraft());
    setDraft({
      code: taxCode.code,
      name: taxCode.name,
      nameAr: taxCode.name_ar ?? "",
      kind: taxCode.kind,
      jurisdiction: taxCode.jurisdiction,
      outputAccountId: taxCode.output_account_id ?? NONE,
      inputAccountId: taxCode.input_account_id ?? NONE,
      active: taxCode.active,
    });
  };

  const startCreate = () => {
    setSelectedId(null);
    setCreating(true);
    setDraft(EMPTY_DRAFT);
    setOpeningRate(emptyRateDraft());
    setFormError(null);
  };

  const createMutation = useIdempotentMutation<TaxCode, TaxCodeCreate>({
    mutationFn: (body, key) => createTaxCode(body, key),
    onSuccess: (taxCode) => {
      void queryClient.invalidateQueries({ queryKey: financeKeys.taxCodes.all });
      selectCode(taxCode);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const updateMutation = useMutation({
    mutationFn: (input: { taxCodeId: string; patch: TaxCodePatch }) =>
      updateTaxCode(input.taxCodeId, input.patch),
    onSuccess: (taxCode) => {
      void queryClient.invalidateQueries({ queryKey: financeKeys.taxCodes.all });
      selectCode(taxCode);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const addRateMutation = useIdempotentMutation<
    TaxCode,
    { taxCodeId: string; rate: TaxRateCreate }
  >({
    mutationFn: (input, key) => addTaxRate(input.taxCodeId, input.rate, key),
    onSuccess: (taxCode) => {
      void queryClient.invalidateQueries({ queryKey: financeKeys.taxCodes.all });
      setNewRate(emptyRateDraft());
      setRateError(null);
      selectCode(taxCode);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  /** Shared validation for both the opening rate and a later rate addition. */
  const buildRate = (rate: RateDraft, kind: TaxCodeKind): TaxRateCreate | string => {
    const percent = normalizeDecimal(rate.percent, { scale: PERCENT_SCALE });
    if (percent === null) {
      return t(
        "finance.taxCodes.percentInvalid",
        'Enter a percentage between 0 and 100 with at most six decimals. "15" means 15%.',
      );
    }
    if (ZERO_RATE_KINDS.has(kind) && !isZeroDecimal(percent, PERCENT_SCALE)) {
      return t(
        "finance.taxCodes.zeroRateRequired",
        "A zero-rated or exempt code must carry a zero rate.",
      );
    }
    if (rate.validFrom === "") {
      return t("finance.taxCodes.validFromRequired", "A rate needs the date it takes effect.");
    }
    if (rate.validTo !== "" && rate.validTo < rate.validFrom) {
      return t("finance.taxCodes.validRangeInvalid", "The end date cannot precede the start date.");
    }
    return {
      percent,
      valid_from: rate.validFrom,
      ...(rate.validTo === "" ? {} : { valid_to: rate.validTo }),
    };
  };

  const submitCreate = () => {
    if (draft.code.trim() === "" || draft.name.trim() === "") {
      setFormError(t("finance.taxCodes.codeNameRequired", "A tax code needs a code and a name."));
      return;
    }
    if (draft.jurisdiction.trim() === "") {
      setFormError(t("finance.taxCodes.jurisdictionRequired", "Name the jurisdiction."));
      return;
    }
    // A reverse-charge code posts an output AND an input leg, so it needs both
    // control accounts before it can be used at all.
    if (
      draft.kind === "reverse_charge" &&
      (draft.outputAccountId === NONE || draft.inputAccountId === NONE)
    ) {
      setFormError(
        t(
          "finance.taxCodes.reverseChargeAccounts",
          "A reverse-charge code posts both legs, so it needs both control accounts.",
        ),
      );
      return;
    }
    const rates: TaxRateCreate[] = [];
    if (openingRate.percent.trim() !== "") {
      const built = buildRate(openingRate, draft.kind);
      if (typeof built === "string") {
        setFormError(built);
        return;
      }
      rates.push(built);
    }
    setFormError(null);
    createMutation.mutate({
      code: draft.code.trim(),
      name: draft.name.trim(),
      ...(draft.nameAr.trim() === "" ? {} : { name_ar: draft.nameAr.trim() }),
      kind: draft.kind,
      jurisdiction: draft.jurisdiction.trim(),
      active: draft.active,
      ...(draft.outputAccountId === NONE ? {} : { output_account_id: draft.outputAccountId }),
      ...(draft.inputAccountId === NONE ? {} : { input_account_id: draft.inputAccountId }),
      ...(rates.length === 0 ? {} : { rates }),
    });
  };

  const submitUpdate = () => {
    if (selectedId === null) return;
    if (draft.name.trim() === "") {
      setFormError(t("finance.taxCodes.codeNameRequired", "A tax code needs a code and a name."));
      return;
    }
    setFormError(null);
    // `null` clears a control account; omitting the member leaves it unchanged.
    updateMutation.mutate({
      taxCodeId: selectedId,
      patch: {
        name: draft.name.trim(),
        // An empty box means "no Arabic name", which the wire spells as null.
        name_ar: draft.nameAr.trim() === "" ? null : draft.nameAr.trim(),
        jurisdiction: draft.jurisdiction.trim(),
        active: draft.active,
        output_account_id: draft.outputAccountId === NONE ? null : draft.outputAccountId,
        input_account_id: draft.inputAccountId === NONE ? null : draft.inputAccountId,
      },
    });
  };

  const submitRate = () => {
    const selected = detailQuery.data;
    if (selected === undefined) return;
    const built = buildRate(newRate, selected.kind);
    if (typeof built === "string") {
      setRateError(built);
      return;
    }
    setRateError(null);
    addRateMutation.mutate({ taxCodeId: selected.id, rate: built });
  };

  const renderPercent = (percent: string): string => {
    try {
      return `${formatQuantity(percent, i18n.language, PERCENT_SCALE)}%`;
    } catch {
      return `${percent}%`;
    }
  };

  const columns = useMemo<ColumnDef<TaxCode>[]>(
    () => [
      {
        accessorKey: "code",
        id: "code",
        header: t("finance.taxCodes.columns.code", "Code"),
        enableSorting: false,
        cell: (ctx) => (
          <span dir="ltr" className="font-mono font-medium">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "name",
        id: "name",
        header: t("finance.taxCodes.columns.name", "Name"),
        enableSorting: false,
      },
      {
        accessorKey: "kind",
        id: "kind",
        header: t("finance.taxCodes.columns.kind", "Kind"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string>();
          return badgeCell(value, KIND_VARIANTS, kindLabel(value));
        },
      },
      {
        accessorKey: "jurisdiction",
        id: "jurisdiction",
        header: t("finance.taxCodes.columns.jurisdiction", "Jurisdiction"),
        enableSorting: false,
        cell: (ctx) => (
          <span dir="ltr" className="font-mono">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        id: "current_rate",
        header: t("finance.taxCodes.columns.currentRate", "Rate today"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => {
          const rate = rateInForce(ctx.row.original.rates, today);
          return rate === undefined ? textCell(null) : renderPercent(rate.percent);
        },
      },
      {
        accessorKey: "active",
        id: "active",
        header: t("finance.taxCodes.columns.active", "Active"),
        enableSorting: false,
        cell: (ctx) =>
          ctx.getValue<boolean>()
            ? badgeCell("true", { true: "success" }, t("finance.common.active", "Active"))
            : badgeCell("false", { false: "secondary" }, t("finance.common.inactive", "Inactive")),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- label helpers are stable per render pass
    [t, i18n.language, today],
  );

  const selected = detailQuery.data;
  const editing = selectedId !== null && selected !== undefined;
  const kindOptions = TAX_CODE_KINDS.map((kind) => ({ value: kind, label: kindLabel(kind) }));

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader
        title={t("finance.taxCodes.title", "Tax codes")}
        description={t(
          "finance.taxCodes.subtitle",
          "Company tax codes, their control accounts and their effective-dated rate history.",
        )}
        actions={
          canCreate ? (
            <Button type="button" onClick={startCreate}>
              <Plus className="size-4" aria-hidden="true" />
              {t("finance.taxCodes.newCode", "New tax code")}
            </Button>
          ) : null
        }
      />

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <SelectField
            id="tax-code-active"
            label={t("finance.taxCodes.filterActive", "Status")}
            value={activeFilter}
            onValueChange={setActiveFilter}
            className="w-44"
            options={[
              { value: ALL, label: t("finance.common.all", "All") },
              { value: "true", label: t("finance.common.active", "Active") },
              { value: "false", label: t("finance.common.inactive", "Inactive") },
            ]}
          />
          <TextField
            id="tax-code-jurisdiction"
            label={t("finance.taxCodes.filterJurisdiction", "Jurisdiction")}
            value={jurisdictionFilter}
            onChange={setJurisdictionFilter}
            placeholder="SA"
            ltr
            className="w-44"
          />
        </CardContent>
      </Card>

      <DataTable
        columns={columns}
        query={listQuery}
        getRowId={(row) => row.id}
        onRowClick={selectCode}
        storageKey="finance.taxCodes.columns"
        emptyMessage={t("finance.taxCodes.empty", "No tax codes match these filters.")}
      />

      {creating || editing ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>
                <h2>
                  {creating
                    ? t("finance.taxCodes.createTitle", "New tax code")
                    : t("finance.taxCodes.editTitle", "Edit {{code}}", { code: draft.code })}
                </h2>
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <TextField
                id="tax-code-code"
                label={t("finance.taxCodes.code", "Code")}
                value={draft.code}
                onChange={(code) => setDraft((current) => ({ ...current, code }))}
                disabled={!creating}
                hint={
                  creating
                    ? undefined
                    : t(
                        "finance.taxCodes.codeFrozen",
                        "Frozen after creation — documents are filed under it.",
                      )
                }
                ltr
              />
              <TextField
                id="tax-code-name"
                label={t("finance.taxCodes.name", "Name")}
                value={draft.name}
                onChange={(name) => setDraft((current) => ({ ...current, name }))}
                disabled={!creating && !canUpdate}
              />
              <TextField
                id="tax-code-name-ar"
                label={t("finance.taxCodes.nameAr", "Name (Arabic)")}
                value={draft.nameAr}
                onChange={(nameAr) => setDraft((current) => ({ ...current, nameAr }))}
                disabled={!creating && !canUpdate}
                hint={t(
                  "finance.taxCodes.nameArHint",
                  "Optional. Shown to operators working in Arabic.",
                )}
              />
              {creating ? (
                <SelectField
                  id="tax-code-kind"
                  label={t("finance.taxCodes.kindLabel", "Kind")}
                  value={draft.kind}
                  onValueChange={(kind) =>
                    setDraft((current) => ({ ...current, kind: kind as TaxCodeKind }))
                  }
                  options={kindOptions}
                  hint={t(
                    "finance.taxCodes.kindHint",
                    "Decides how many ledger legs the code posts. Frozen after creation.",
                  )}
                />
              ) : (
                <Field id="tax-code-kind-static" label={t("finance.taxCodes.kindLabel", "Kind")}>
                  <p id="tax-code-kind-static" className="pt-2 text-sm font-medium">
                    {kindLabel(draft.kind)}
                  </p>
                </Field>
              )}
              <TextField
                id="tax-code-jurisdiction-field"
                label={t("finance.taxCodes.jurisdiction", "Jurisdiction")}
                value={draft.jurisdiction}
                onChange={(jurisdiction) => setDraft((current) => ({ ...current, jurisdiction }))}
                disabled={!creating && !canUpdate}
                hint={t(
                  "finance.taxCodes.jurisdictionHint",
                  "ISO country, optionally with a subdivision (SA, SA-01).",
                )}
                ltr
              />
              <SelectField
                id="tax-code-output-account"
                label={t("finance.taxCodes.outputAccount", "Output tax account")}
                value={draft.outputAccountId}
                onValueChange={(outputAccountId) =>
                  setDraft((current) => ({ ...current, outputAccountId }))
                }
                options={accountOptions}
                disabled={!canReadAccounts || (!creating && !canUpdate)}
                hint={t("finance.taxCodes.outputAccountHint", "Credited by sales-side postings.")}
              />
              <SelectField
                id="tax-code-input-account"
                label={t("finance.taxCodes.inputAccount", "Input tax account")}
                value={draft.inputAccountId}
                onValueChange={(inputAccountId) =>
                  setDraft((current) => ({ ...current, inputAccountId }))
                }
                options={accountOptions}
                disabled={!canReadAccounts || (!creating && !canUpdate)}
                hint={t("finance.taxCodes.inputAccountHint", "Debited by purchase-side postings.")}
              />

              <label className="flex items-center gap-2 text-sm sm:col-span-2">
                <Checkbox
                  checked={draft.active}
                  disabled={!creating && !canUpdate}
                  onCheckedChange={(checked) =>
                    setDraft((current) => ({ ...current, active: checked === true }))
                  }
                />
                {t("finance.taxCodes.activeLabel", "Available for new documents")}
              </label>

              {creating ? (
                <div className="grid grid-cols-1 gap-3 border-t pt-4 sm:col-span-2 sm:grid-cols-3">
                  <p className="text-sm text-muted-foreground sm:col-span-3">
                    {t(
                      "finance.taxCodes.openingRateHint",
                      "Opening rate. The code and its rate are created in one transaction, so a code is never persisted unpriced.",
                    )}
                  </p>
                  <DecimalField
                    id="tax-code-opening-percent"
                    label={t("finance.taxCodes.percent", "Percent")}
                    value={openingRate.percent}
                    onChange={(percent) => setOpeningRate((current) => ({ ...current, percent }))}
                    decimal={{ scale: PERCENT_SCALE }}
                    placeholder="15"
                    invalidMessage={t(
                      "finance.taxCodes.percentInvalid",
                      'Enter a percentage between 0 and 100 with at most six decimals. "15" means 15%.',
                    )}
                  />
                  <DateField
                    id="tax-code-opening-from"
                    label={t("finance.taxCodes.validFrom", "Effective from")}
                    value={openingRate.validFrom}
                    onChange={(validFrom) =>
                      setOpeningRate((current) => ({ ...current, validFrom }))
                    }
                  />
                  <DateField
                    id="tax-code-opening-to"
                    label={t("finance.taxCodes.validTo", "Effective to")}
                    value={openingRate.validTo}
                    onChange={(validTo) => setOpeningRate((current) => ({ ...current, validTo }))}
                    hint={t("finance.taxCodes.validToHint", "Leave empty for an open-ended rate.")}
                  />
                </div>
              ) : null}

              {formError === null ? null : (
                <div className="sm:col-span-2">
                  <ProblemBanner>{formError}</ProblemBanner>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
                {creating ? (
                  <Button
                    type="button"
                    loading={createMutation.isPending}
                    disabled={!canCreate}
                    onClick={submitCreate}
                  >
                    {t("finance.taxCodes.create", "Create tax code")}
                  </Button>
                ) : canUpdate ? (
                  <Button type="button" loading={updateMutation.isPending} onClick={submitUpdate}>
                    {t("finance.taxCodes.save", "Save tax code")}
                  </Button>
                ) : null}
                <Button type="button" variant="ghost" onClick={resetForms}>
                  {t("actions.cancel")}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                <h2>{t("finance.taxCodes.ratesTitle", "Rate history")}</h2>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {selected === undefined ? (
                <p className="text-sm text-muted-foreground">
                  {t(
                    "finance.taxCodes.ratesAfterCreate",
                    "Rate history appears once the code exists.",
                  )}
                </p>
              ) : (
                <>
                  <ul className="flex flex-col divide-y">
                    {selected.rates.length === 0 ? (
                      <li className="py-2 text-sm text-muted-foreground">
                        {t("finance.taxCodes.noRates", "No rates yet.")}
                      </li>
                    ) : (
                      selected.rates.map((rate) => {
                        const inForce = rateInForce(selected.rates, today)?.id === rate.id;
                        return (
                          <li
                            key={rate.id}
                            className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"
                          >
                            <span dir="ltr" className="font-mono font-medium tabular-nums">
                              {renderPercent(rate.percent)}
                            </span>
                            <span className="text-muted-foreground">
                              <span dir="ltr">{rate.valid_from}</span>
                              {" → "}
                              <span dir="ltr">
                                {rate.valid_to ?? t("finance.taxCodes.openEnded", "open-ended")}
                              </span>
                            </span>
                            {inForce ? (
                              <span className="rounded-md border border-success/40 bg-success/10 px-2 py-0.5 text-xs">
                                {t("finance.taxCodes.inForce", "In force today")}
                              </span>
                            ) : null}
                          </li>
                        );
                      })
                    )}
                  </ul>

                  {canUpdate ? (
                    <div className="grid grid-cols-1 gap-3 border-t pt-4 sm:grid-cols-3">
                      <p className="text-sm text-muted-foreground sm:col-span-3">
                        {t(
                          "finance.taxCodes.addRateHint",
                          "A rate change is a new row, never an edit. Overlapping validity for one code is refused.",
                        )}
                      </p>
                      <DecimalField
                        id="tax-code-new-percent"
                        label={t("finance.taxCodes.percent", "Percent")}
                        value={newRate.percent}
                        onChange={(percent) => setNewRate((current) => ({ ...current, percent }))}
                        decimal={{ scale: PERCENT_SCALE }}
                        placeholder="15"
                        invalidMessage={t(
                          "finance.taxCodes.percentInvalid",
                          'Enter a percentage between 0 and 100 with at most six decimals. "15" means 15%.',
                        )}
                      />
                      <DateField
                        id="tax-code-new-from"
                        label={t("finance.taxCodes.validFrom", "Effective from")}
                        value={newRate.validFrom}
                        onChange={(validFrom) =>
                          setNewRate((current) => ({ ...current, validFrom }))
                        }
                      />
                      <DateField
                        id="tax-code-new-to"
                        label={t("finance.taxCodes.validTo", "Effective to")}
                        value={newRate.validTo}
                        onChange={(validTo) => setNewRate((current) => ({ ...current, validTo }))}
                        hint={t(
                          "finance.taxCodes.validToHint",
                          "Leave empty for an open-ended rate.",
                        )}
                      />
                      {rateError === null ? null : (
                        <div className="sm:col-span-3">
                          <ProblemBanner>{rateError}</ProblemBanner>
                        </div>
                      )}
                      <div className="sm:col-span-3">
                        <Button
                          type="button"
                          loading={addRateMutation.isPending}
                          onClick={submitRate}
                        >
                          {t("finance.taxCodes.addRate", "Add rate")}
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
